import * as React from 'react';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import type { DateRange } from 'react-day-picker';

import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '@/lib/cn';

/** Single-date picker — opens a calendar in a popover when clicked. */
export function DatePicker({
  value,
  onChange,
  placeholder = 'Wybierz datę',
  disabled,
  fromDate,
  toDate,
  disabledDates,
  className,
}: {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  fromDate?: Date;
  toDate?: Date;
  disabledDates?: (d: Date) => boolean;
  className?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal',
            !value && 'text-text-muted',
            className,
          )}
        >
          <CalendarIcon className="size-4" aria-hidden="true" />
          {value ? format(value, 'EEEE, d MMMM yyyy', { locale: pl }) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          startMonth={fromDate}
          endMonth={toDate}
          disabled={disabledDates}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}

/** Date-range picker for holidays / closures. */
export function DateRangePicker({
  value,
  onChange,
  placeholder = 'Wybierz zakres dat',
  disabled,
  className,
}: {
  value?: DateRange;
  onChange: (range: DateRange | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const label = (() => {
    if (!value?.from) return placeholder;
    if (!value.to || value.from.getTime() === value.to.getTime()) {
      return format(value.from, 'd MMM yyyy', { locale: pl });
    }
    return `${format(value.from, 'd MMM', { locale: pl })} – ${format(value.to, 'd MMM yyyy', { locale: pl })}`;
  })();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal',
            !value?.from && 'text-text-muted',
            className,
          )}
        >
          <CalendarIcon className="size-4" aria-hidden="true" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={value}
          onSelect={onChange}
          numberOfMonths={2}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
